// cables.json carries only the BoQ; the per-circuit geometry lives in the agent's
// per-plan "view bundle" (agent_output/cables/view/<anndir>.json). We fetch that,
// read cached_batch.circuits[].destinations[].path_coords (annotation-PIXEL space)
// and cached_batch.sources (board positions), convert to PDF-point space
// (÷ px_per_pt = annotation-px-per-PDF-point from the bundle, falling back to
// imageScaleFactor for older bundles), and shape it into the same
// CircuitPathsState the panel already renders for tray routes.
//
// Returns `all` (every circuit) and `byCircuit` (one entry per circuit, each with
// its source board as distributionBoardPosition) so the UI can isolate a single
// circuit + show its source distinctly.

import type {
  CircuitPathsState,
  CircuitPathResult,
  RouteWaypoint,
} from "../../../ReviewPage/hooks/useCircuitCablePaths/types";
import { fetchProjectAsset } from "components/SmartSurveyor/SmartSurveyorV3/utils/signedAssetUrls";
import type { CableSystem } from "../fetchCables";
import { PALETTE, SPECIAL_PANELS } from "./constants";
import { buildBoardCandidates } from "../../CableTakeoffTabV3/boardCandidates";
import type { BoardCandidate, NamedPosition } from "../../CableTakeoffTabV3/boardCandidates";
import { isSameBoard } from "../../CableTakeoffTabV3/boardNames";
import { suggestMainBoard } from "../../CableTakeoffTabV3/mainBoard";
import { categoryOfAgentSystem, categoryOfDeviceName } from "../../CableTakeoffTabV3/deviceCategories";
import type { DeviceType } from "../../CableTakeoffTabV3/deviceCategories";
import type {
  AgentTrayGraph,
  CableCircuitDefaults,
  AgentViewBundle,
  CableCircuitTray,
  CableSourceMarker,
  CableTakeoffGeometry,
  CableTraySegment,
} from "./types";

/** Match a special (non-UV) panel by name, or null for normal boards. */
function specialPanel(
  name: string,
): { label: string; system?: CableSystem } | null {
  for (const p of SPECIAL_PANELS) {
    if (p.re.test(name)) return { label: p.label, system: p.system };
  }
  return null;
}

// Compact board label for the marker. Special panels (BMZ/PA/ZLT/…) keep their
// own token; normal boards get a "UV<n>" tag, e.g. "UV/MV WE 11" -> "UV11",
// "Unterverteiler #3" -> "UV3", "UV Wohnung" -> "UV", else a 3-char token.
// Exported so consumers (e.g. SourceToolbar) match a provenance source's full
// board name back to a marker label using the IDENTICAL logic (no divergence).
export function shortBoardLabel(name: string): string {
  const special = specialPanel(name);
  if (special) return special.label;
  const nums = name.match(/\d+/g);
  if (nums && nums.length) return `UV${nums[nums.length - 1]}`;
  if (/UV/i.test(name)) return "UV";
  const tok = name
    .replace(/[^A-Za-z0-9]/g, "")
    .slice(0, 3)
    .toUpperCase();
  return tok || "S";
}

function viewBundleKey(
  projectId: string,
  annotationKey: string,
): string | null {
  const anndir = annotationKey
    .split("/annotations/")[1]
    ?.split("/counts.json")[0];
  if (!anndir) return null;
  // Signed URLs are fresh per page load, so a reload always pulls the latest
  // view bundle from S3, not a stale cached copy.
  return `smart-surveyor/projects/${projectId}/agent_output/cables/view/${anndir}.json`;
}

/**
 * The tray network, flattened to drawn lengths. A graph is what the run needs to
 * decide which run rides which tray; the drawing needs lines, and one edge is
 * one length of tray however the connectivity is later rebuilt.
 *
 * An edge whose endpoints are not both in `nodes` is dropped rather than
 * guessed: half a tray drawn to a point nobody published is worse than none.
 */
function readTrays(
  graph: AgentTrayGraph | undefined,
  annotationPixelsPerPdfPoint: number,
): CableTraySegment[] {
  // Both guarded: a malformed `tray` key must cost the trays, never the whole
  // takeoff. Throwing here would return null from the fetch and take all 83
  // runs off the plan with it.
  const nodes =
    graph?.nodes && typeof graph.nodes === "object" ? graph.nodes : {};
  const edges = Array.isArray(graph?.edges) ? graph.edges : [];
  const at = (id: string | undefined): RouteWaypoint | null => {
    const node = id === undefined ? undefined : nodes[id];
    if (!node) return null;
    const px = node.pixel_coordinate?.[0] ?? node.x;
    const py = node.pixel_coordinate?.[1] ?? node.y;
    if (typeof px !== "number" || typeof py !== "number") return null;
    return {
      x: px / annotationPixelsPerPdfPoint,
      y: py / annotationPixelsPerPdfPoint,
    };
  };
  const segments: CableTraySegment[] = [];
  edges.forEach((edge) => {
    const from = at(edge.from);
    const to = at(edge.to);
    if (!from || !to) return;
    segments.push({ from, to, type: edge.tray_type?.trim() || "" });
  });
  return segments;
}

const namedPositions = (
  entries: Array<[string | undefined, number | undefined, number | undefined]>,
): NamedPosition[] =>
  entries.flatMap(([name, x, y]) =>
    name && typeof x === "number" && typeof y === "number" ? [{ name, x, y }] : [],
  );

// The agent's label wins; the name is read only when it never labelled that type.
const deviceTypesOf = (bundle: AgentViewBundle | null): Record<string, DeviceType> => {
  const labels = bundle?.system_labels?.labels?.devices ?? {};
  const labelByName = new Map(
    Object.entries(labels).map(([name, label]) => [name.trim().toLowerCase(), label.system]),
  );
  const names: Array<[string, string]> = [
    ...Object.entries(bundle?.elements ?? {}).map(
      ([id, element]): [string, string] => [id, element?.name ?? ""],
    ),
    ...(bundle?.devices ?? []).map((device): [string, string] => [device.id ?? "", device.name ?? ""]),
  ];
  const byElement: Record<string, DeviceType> = {};
  names.forEach(([id, rawName]) => {
    const name = rawName.trim();
    if (!id || !name || byElement[id]) return;
    const fromAgent = categoryOfAgentSystem(labelByName.get(name.toLowerCase()));
    byElement[id] = fromAgent
      ? { name, category: fromAgent, from: "agent" }
      : { name, category: categoryOfDeviceName(name), from: "name" };
  });
  return byElement;
};

/** Run boards keep the label they always had, so edits stored under it still land; later ones get a suffix. */
const markersOf = (
  candidates: BoardCandidate[],
  agentSystems: Record<string, string>,
): CableSourceMarker[] => {
  const taken = new Set<string>();
  return candidates.map((candidate) => {
    const special = specialPanel(candidate.name);
    const base = special ? special.label : shortBoardLabel(candidate.name);
    let label = base;
    for (let n = 2; taken.has(label); n += 1) label = `${base}-${n}`;
    taken.add(label);
    return {
      x: candidate.position.x,
      y: candidate.position.y,
      label,
      name: candidate.name,
      usedByRun: candidate.usedByRun,
      origin: candidate.origin,
      ...(special?.system ? { system: special.system } : {}),
      agentSystems: Object.entries(agentSystems)
        .filter(
          ([board, system]) =>
            system && isSameBoard({ board, name: candidate.name, label }),
        )
        .map(([, system]) => system),
    };
  });
};

export async function fetchCableTakeoffPaths(
  projectId: string,
  annotationKey: string,
  imageScaleFactor: number,
): Promise<CableTakeoffGeometry | null> {
  const key = viewBundleKey(projectId, annotationKey);
  if (!key) return null;
  let bundle: AgentViewBundle | null = null;
  try {
    const res = await fetchProjectAsset({ projectId, key });
    if (!res.ok) return null;
    bundle = (await res.json()) as AgentViewBundle;
  } catch {
    return null;
  }
  const circuits = bundle?.cached_batch?.circuits ?? [];
  const rawSources = bundle?.cached_batch?.sources ?? {};
  // Annotation-pixel → PDF-point divisor
  const annotationPixelsPerPdfPoint =
    bundle?.px_per_pt || imageScaleFactor || 1;

  // Board source positions (PDF space) keyed by board name.
  const sourceByBoard: Record<string, RouteWaypoint> = {};
  Object.entries(rawSources).forEach(([name, s]) => {
    const px = s.pixel_coordinate?.[0] ?? s.x;
    const py = s.pixel_coordinate?.[1] ?? s.y;
    if (typeof px === "number" && typeof py === "number") {
      sourceByBoard[name] = {
        x: px / annotationPixelsPerPdfPoint,
        y: py / annotationPixelsPerPdfPoint,
      };
    }
  });

  const trays = readTrays(bundle?.tray, annotationPixelsPerPdfPoint);

  // Keyed once rather than searched per circuit: an 80-circuit plan would
  // otherwise be quadratic for no reason.
  const assignmentByCircuit = new Map(
    (bundle?.circuit_assignment?.circuits ?? [])
      .filter((a) => a.circuit_id)
      .map((a) => [a.circuit_id as string, a]),
  );

  const allPaths: CircuitPathResult[] = [];
  const byCircuit: Record<string, CircuitPathsState> = {};
  const defaults: Record<string, CableCircuitDefaults> = {};
  const trayByCircuit: Record<string, CableCircuitTray> = {};

  circuits.forEach((c, ci) => {
    const rgb = PALETTE[ci % PALETTE.length];
    const cid = c.circuit_id ?? `c${ci}`;
    const cPaths: CircuitPathResult[] = [];
    (c.destinations ?? []).forEach((d, di) => {
      const pc = d.path_coords ?? [];
      if (pc.length < 2) return;
      const waypoints = pc.map((p) => ({
        x: p.x / annotationPixelsPerPdfPoint,
        y: p.y / annotationPixelsPerPdfPoint,
      }));
      const xs = waypoints.map((w) => w.x);
      const ys = waypoints.map((w) => w.y);
      cPaths.push({
        routeId: cid,
        destinationId: `${cid}-${di}`,
        // The address stays for geometry; the identity travels beside it. Both
        // halves of destinationId are run-scoped, so a persisted edit keyed on
        // it is orphaned by the next re-run — element_id is not.
        elementId: d.element_id,
        destinationName: cid,
        waypoints,
        trayMeters: 0,
        totalMeters: c.total_cable_length_m ?? 0,
        reachable: true,
        rgb,
        coordinateExtents: {
          minX: Math.min(...xs),
          minY: Math.min(...ys),
          maxX: Math.max(...xs),
          maxY: Math.max(...ys),
        },
      });
    });
    // Both halves off the SAME circuit object: paired with a length from
    // anywhere else, an edit could move one and not the other and the
    // proportion would quietly drift.
    const onTrayM = c.on_tray_length_m;
    const billedM = c.billed_cable_length_m;
    if (typeof onTrayM === "number" && typeof billedM === "number") {
      trayByCircuit[cid] = { onTrayM, billedM };
    }
    if (cPaths.length === 0) return;
    // The per-circuit FACTS come from the assignment where the run publishes
    // one, and from the circuit itself where it does not. They are derived from
    // the same batch, so the two agree by construction while both are live —
    // and when `cached_batch` finally goes, only the first path is left.
    const facts = assignmentByCircuit.get(cid) ?? c;
    defaults[cid] = {
      noTraySurchargePct: facts.no_tray_surcharge_pct ?? 0,
    };
    allPaths.push(...cPaths);
    // Sources shown via `sources`/boardMarkers, so keep this null (no dup marker).
    byCircuit[cid] = { paths: cPaths, distributionBoardPosition: null };
  });

  if (allPaths.length === 0) return null;
  const candidates = buildBoardCandidates({
    runSources: sourceByBoard,
    boardMarkers: namedPositions(
      (bundle?.board_markers ?? []).map((marker) => [marker.name, marker.x, marker.y]),
    ),
    viewBoards: namedPositions(
      Object.entries(bundle?.boards ?? {}).map(([name, at]) => [name, at?.[0], at?.[1]]),
    ),
    boardText: namedPositions(
      Object.entries(bundle?.board_text_loc ?? {}).map(([name, at]) => [name, at?.[0], at?.[1]]),
    ),
    annotationPixelsPerPdfPoint,
  });
  const sources = markersOf(candidates, bundle?.provenance?.board_roles?.systems ?? {});
  const labelOfBoard = (board: string | undefined): string | null => {
    if (!board) return null;
    const exact = sources.find((source) => source.name === board);
    if (exact) return exact.label;
    return (
      sources.find((source) => isSameBoard({ board, name: source.name, label: source.label }))
        ?.label ?? null
    );
  };

  const runBoardByCircuit: Record<string, string> = {};
  const powerCircuitCountByBoard: Record<string, number> = {};
  circuits.forEach((c, ci) => {
    const cid = c.circuit_id ?? `c${ci}`;
    if (!byCircuit[cid]) return;
    const board = c.source_id ?? c.board_assigned;
    const label = labelOfBoard(board);
    if (!label || !board) return;
    runBoardByCircuit[cid] = label;
    if (!specialPanel(board)) {
      powerCircuitCountByBoard[board] = (powerCircuitCountByBoard[board] ?? 0) + 1;
    }
  });

  const suggestion = suggestMainBoard({
    roles: bundle?.provenance?.board_roles,
    sourceQuestions: bundle?.provenance?.source_questions,
    boardNames: candidates.map((candidate) => candidate.name),
    powerCircuitCountByBoard,
  });
  const suggestedLabel = suggestion ? labelOfBoard(suggestion.name) : null;

  return {
    all: { paths: allPaths, distributionBoardPosition: null },
    byCircuit,
    sources,
    trays,
    trayByCircuit,
    defaults,
    runBoardByCircuit,
    deviceTypeByElementId: deviceTypesOf(bundle),
    mainBoardSuggestion:
      suggestion && suggestedLabel ? { label: suggestedLabel, from: suggestion.from } : null,
  };
}
